/**
 * Simplify Date Parsing across the app
 * See https://day.js.org/docs/en/display/format for more info about formatting.
 */

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isToday from 'dayjs/plugin/isToday';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import localizedFormat from 'dayjs/plugin/localizedFormat';
dayjs.extend(isToday);
dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(localizedFormat);

export const TODAY = dayjs();
export const TOMORROW = dayjs().add(1, 'day');
export const YESTERDAY = dayjs().subtract(1, 'day');
export const YEAR = dayjs().year();
export const MONTH = dayjs().month();

export const parseDate = (date) => {
  return dayjs(date).format('MM/DD/YYYY');
};

export const parseDateDayOfWeek = (date) => {
  return dayjs(date).format('dddd');
};

export const parseDateLong = (date) => {
  return dayjs(date).format('MMMM DD, YYYY');
};
export const parseDateLongWithTime = (date) => {
  return dayjs(date).format('MMMM DD, YYYY hh:mm A');
};

export const parseDateMonthYearLong = (date) => {
  return dayjs(date).format('MMMM YYYY');
};

export const parseDateShort = (date) => {
  return dayjs(date).format('MMM. DD');
};

export const parseTime = (time) => {
  return dayjs(time, 'HH:mm:ss').format('hh:mm A');
};

export const parseDateTime = (date, time) => {
  return parseDate(date) + ' ' + parseTime(time);
};

export const isDateValid = (date) => {
  return dayjs(date).isValid();
};

export const isDatePastDated = (date) => {
  return dayjs(date).isBefore(TODAY, 'day');
};

export const parseTimeFromDate = (date) => {
  return dayjs(date).format('hh:mm A');
};

export const isDateToday = (date) => {
  return dayjs(date).isToday();
};

export const isDateCurrentAndPastDated = (date) => {
  return dayjs(date).isBefore(TOMORROW, 'day');
};

export const isDateInvalid = (date) => {
  return !isDateValid(date);
};

export const isDateFutureDated = (date) => {
  return !isDateCurrentAndPastDated(date);
};

export const isEqualOrGreaterThanDateFrom = (dateFrom, dateEnd) => {
  return dayjs(dateEnd).isBefore(dateFrom);
};

export const isEqualOrLessThanDateTo = (dateFrom, dateEnd) => {
  return dayjs(dateFrom).isAfter(dateEnd);
};

export const isWithin90Day = (dateFrom, dateTo) => {
  // 89 as 'dateFrom' value is added to make it 90
  const thirdMonth = dayjs(dateFrom).add(89, 'day');
  return !dayjs(dateTo).isBetween(dayjs(dateFrom), thirdMonth, 'day', '[]');
};

export const isWithin31Day = (dateFrom, dateTo) => {
  // 30 as 'dateFrom' value is added to make it 31
  const thirdMonth = dayjs(dateFrom).add(30, 'day');
  return !dayjs(dateTo).isBetween(dayjs(dateFrom), thirdMonth, 'day', '[]');
};

export const isDateExpired = (date) => {
  return dayjs(date).diff(TODAY) <= 0;
};

export const isDateRangeValid = (dateFrom, dateTo) => {
  if (!dateFrom || !dateTo) {
    return false;
  } else if (isDateInvalid(dateFrom) || isDateInvalid(dateTo)) {
    return false;
  } else if (isDateFutureDated(dateFrom) || isDateFutureDated(dateTo)) {
    return false;
  } else {
    return true;
  }
};

export const isFormatStandard = (date) => {
  return dayjs(date, 'MM/DD/YYYY', true).isValid();
};

export const formatDateStandardDashed = (date) => {
  return dayjs(date).format('MM-DD-YYYY');
};

export const fileNameDateFormat = (date) => {
  return dayjs(date).format('MMDDYY');
};

export const getMonth = (date) => {
  return dayjs(date).format('MM');
};

export const getYear = (date) => {
  return dayjs(date).format('YYYY');
};

export const parseDateMonthText = (date) => {
  return dayjs(date).format('MMM. DD, YYYY');
};

export const parseDateMonthTextNoDot = (date) => {
  return dayjs(date).format('MMM DD, YYYY');
};

export const parseDateMonthNoPeriod = (date) => {
  return dayjs(date).format('MMM DD, YYYY');
};

export const formatDateISO = (date) => {
  return dayjs(date, 'MM/DD/YYYY').format('YYYY-MM-DD');
};

export const formatTimeISO = (time) => {
  return dayjs(time, 'hh:mm A').format('HH:mm:ss');
};

export const convertDateTime = (date, time) => {
  let formattedTime = convertTimeFormat(time);
  return `${formatDateISO(date)}T${formatTimeISO(formattedTime)}`;
};

export const isSameDate = (date1, date2) => {
  return dayjs(date1).isSame(date2);
};

export const isTimeValidStrictUpperCase = (time) => {
  return dayjs(time, 'hh:mm A', true).isValid();
};

export const isTimeValidStrictLowerCase = (time) => {
  return dayjs(time, 'hh:mm a', true).isValid();
};

export const convertTimeFormat = (time) => {
  if (isTimeValidStrictLowerCase(time)) {
    return dayjs(time, 'hh:mm a').format('hh:mm A');
  }
  return time;
};

export const isDateBefore = (dateCompare, dateTo) => {
  return dayjs(dateCompare).isBefore(dateTo);
};

export const isDateBeforeOrEqual = (dateCompare, dateTo) => {
  return dayjs(dateCompare).isSameOrBefore(dateTo);
};

export const isDateAfter = (dateCompare, dateFrom) => {
  return dayjs(dateCompare).isAfter(dateFrom);
};

export const isDateAfterOrEqual = (dateCompare, dateFrom) => {
  return dayjs(dateCompare).isSameOrAfter(dateFrom);
};

export const isDateValidYYYYMMDD = (date) => {
  return dayjs(date, 'YYYY-MM-DD', true).isValid();
};

export const isTimeValid24H = (time) => {
  return dayjs(time, 'HH:mm', true).isValid();
};

export const formatYYYYMMDD = (date) => {
  return dayjs(date, 'YYYY-MM-DD', true).format('MM/DD/YYYY');
};

export const convert24HTo12H = (time) => {
  return dayjs(time, 'HH:mm', true).format('hh:mm A');
};

export const last7Days = () => {
  return dayjs(TODAY).subtract(7, 'day');
};

export const last30Days = () => {
  return dayjs(TODAY).subtract(30, 'day');
};

export const getDateString = (date) => {
  if (date) {
    if (getYear(date) === '1901' || getYear(date) === '1900') {
      return '-';
    } else return parseDate(date);
  } else return '-';
};

export const getformatedDate = (date, format) => {
  return dayjs(date).format(format || 'YYYY-MM-DD');
};

export const getDatesFromDateToEndDate = (startDate, endDate) => {
  const dates = [];
  let currentDate = dayjs(startDate) || dayjs(`${dayjs().year()}-01-01`);
  const end = dayjs(endDate) || dayjs(`${dayjs().year()}-12-31`);

  while (currentDate.isSameOrBefore(end)) {
    dates.push(currentDate.format('YYYY-MM-DD'));
    currentDate = currentDate.add(1, 'day');
  }
  return dates;
};

export const getWeekends = (startDate, endDate) => {
  const weekends = [];
  let currentDate = dayjs(startDate);
  const end = dayjs(endDate);

  while (currentDate.isSameOrBefore(end)) {
    if (currentDate.day() === 0 || currentDate.day() === 6) {
      weekends.push(currentDate.format('YYYY-MM-DD'));
    }
    currentDate = currentDate.add(1, 'day');
  }
  return weekends;
};

export const getMonthStartEndDates = (year, month) => {
  // month in Day.js is 0-indexed (0 = January, 11 = December)
  const firstDateOfMonth = dayjs().year(year).month(month).startOf('month');
  const lastDateOfMonth = dayjs().year(year).month(month).endOf('month');

  return { firstDateOfMonth, lastDateOfMonth };
};

export const subtractDays = (noOfDays, date) => {
  if (date) return dayjs(date).subtract(noOfDays, 'days');
  return dayjs().subtract(noOfDays, 'days');
};

export const addDays = (noOfDays, date) => {
  if (date) return dayjs(date).add(noOfDays, 'days');
  return dayjs().add(noOfDays, 'days');
};

export const convertDateTimeFormat = (date, currentFormat) => {
  return dayjs(date, currentFormat).format('MM/DD/YYYY h:mm A');
};

export const convertDateToObject = (date) => {
  return dayjs(date);
};

export const computeHoursBetween = (time1, time2, format) => {
  let timeFormat = format || 'h:mm A';
  let start = dayjs(time1, timeFormat);
  let end = dayjs(time2, timeFormat);

  // Adjust for cases where the end time is on the next day
  if (end.isBefore(start)) {
    end = end.add(1, 'day');
  }

  const diffInHours = end.diff(start, 'hour');
  const diffInMinutes = end.diff(start, 'minute') % 60;
  const totalMinutes = end.diff(start, 'minute');

  if (diffInHours <= 0 && diffInMinutes > 0) return { inText: `${diffInMinutes} min(s)`, totalMinutes: totalMinutes };
  if (diffInHours > 0 && diffInMinutes <= 0) return { inText: `${diffInHours} hr(s)`, totalMinutes: totalMinutes };

  return {
    inText: ` ${diffInHours} hr(s) ${diffInMinutes} min(s)`,
    totalMinutes: totalMinutes,
  };
};

export const isTimeBetween = (time, startTime, endTime) => {
  const format = 'hh:mm A';
  let timeToCheck = dayjs(time, format);
  let start = dayjs(startTime, format);
  let end = dayjs(endTime, format);

  // Return true if time is between startTime and endTime, considering edge cases
  return start.isBefore(end)
    ? timeToCheck.isBetween(start, end)
    : timeToCheck.isAfter(start) || timeToCheck.isBefore(end);
};

export const isStartTimeBeforeShift = (time, shiftStartTime) => {
  const format = 'hh:mm A';
  let checkTime = dayjs(time.toUpperCase(), format);
  let startTime = dayjs(shiftStartTime.toUpperCase(), format);

  return checkTime.isBefore(startTime);
};

export const getTimeDuration = (start, end) => {
  const format = 'YYYY-MM-DD HH:mm:ss';
  const startTime = dayjs(start, format);
  const endTime = dayjs(end, format);

  return endTime.diff(startTime, 'minute');
};

export const isTimeAfterShift = (time, shiftEnd) => {
  const format = 'hh:mm A';
  const timeToCheck = dayjs(time, format);
  let end = dayjs(shiftEnd, format);

  // Now, check if the given time is after the shift end time
  return timeToCheck.isAfter(end);
};

export const toFormatISOString = (date) => {
  return dayjs(date).set('hour', 10).set('minute', 34).set('second', 56).set('millisecond', 44).toISOString();
};

export const getBirthdayThisYear = (birthday) => {
  const currentYear = dayjs().year();
  const birthDate = dayjs(birthday).year(currentYear);

  return birthDate.format('YYYY-MM-DD');
};

export const getWeekRange = (date) => {
  const targetDate = dayjs(date);
  const dayOfWeek = targetDate.day(); // Get the day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)

  // Calculate the first date of the week (Sunday)
  const firstDate = targetDate.subtract(dayOfWeek, 'day').format('YYYY-MM-DD');

  // Calculate the last date of the week (Saturday)
  const lastDate = targetDate.add(6 - dayOfWeek, 'day').format('YYYY-MM-DD');

  return { firstDate, lastDate };
};

export const isDateBetween = (date, startDate, endDate) => {
  const targetDate = dayjs(date);
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  return targetDate.isBetween(start, end, null, '[]'); // '[]' includes the start and end dates
};

export const countConsecutiveDays = (dates) => {
  if (dates.length === 0) return 0;

  // Parse dates into Day.js objects and sort them in ascending order
  const timestamps = dates.map((date) => dayjs(date)).sort((a, b) => a.valueOf() - b.valueOf());

  let consecutiveDays = 1;
  let maxConsecutiveDays = 1;

  // Iterate through the sorted timestamps and count consecutive days
  for (let i = 1; i < timestamps.length; i++) {
    const diffInDays = timestamps[i].diff(timestamps[i - 1], 'day');
    const prevDay = timestamps[i - 1].day();
    const currDay = timestamps[i].day();

    // Check if the difference is 1 day, a Saturday to Monday transition, or a Friday to Monday transition
    if (
      diffInDays === 1 ||
      (prevDay === 6 && currDay === 1) || // Saturday to Monday
      (prevDay === 5 && currDay === 1 && diffInDays === 3) // Friday to Monday
    ) {
      consecutiveDays++;
      maxConsecutiveDays = Math.max(maxConsecutiveDays, consecutiveDays);
    } else {
      consecutiveDays = 1;
    }
  }

  return maxConsecutiveDays;
};

export const suggestBreakTime = (shiftStart, shiftEnd) => {
  const baseDate = getformatedDate(TODAY); // Any arbitrary reference date
  const parseTime = (timeStr, isNextDay = false) => {
    const date = dayjs(`${baseDate} ${timeStr}`);
    return isNextDay ? date.add(1, 'day') : date;
  };

  let start = parseTime(shiftStart);
  let end = parseTime(shiftEnd);

  // Handle cross-midnight shifts
  if (end.isBefore(start)) {
    end = parseTime(shiftEnd, true);
  }

  const totalShiftHours = end.diff(start, 'hour', true); // accurate to decimal

  if (totalShiftHours <= 1) return { breakStart: null, breakEnd: null }; // No break time for shifts less than 1 hour

  const breakStart = start.add(totalShiftHours / 2, 'hour');
  const breakEnd = breakStart.add(1, 'hour');

  return {
    breakStart: breakStart.format('h:mm A'),
    breakEnd: breakEnd.format('h:mm A'),
  };
};
